import ColorField from "../components/ColorField";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import axios, { AxiosError } from "axios";
import { AlertCircle, Loader2 } from "lucide-react";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import { app } from "../firebase";
import { API_BASE_URL } from "../utils/advancedSettings";
import { loginHeader } from "../utils/loginHeader";
import { fetchQuota, StreamQuota } from "../utils/streamRequests";
import { useStreams } from "../hooks/useStreams";
import { trackClick } from "../utils/analytics";

const DEFAULT_LOGO = "https://cdn-icons-png.flaticon.com/512/2395/2395089.png";
const DEFAULT_COLOR = "#888888";

const AddStreamPage: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(getAuth(app));
  const { select, reload } = useStreams();

  const [quota, setQuota] = useState<StreamQuota | null>(null);
  const [name, setName] = useState("");
  const [logo, setLogo] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [nameError, setNameError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchQuota(() => user.getIdToken()).then(setQuota).catch((error) => console.error(error));
  }, [user]);

  const onName = (value: string) => {
    setName(value);
    if (/[^a-zA-Z0-9]/.test(value)) setNameError("Use letters and numbers only, with no spaces.");
    else if (value.length > 0 && value.length < 3) setNameError("Stream name must be at least 3 characters.");
    else setNameError("");
  };

  const errorText = (error: unknown, fallback: string) =>
    error instanceof AxiosError && typeof error.response?.data === "string" ? error.response.data : fallback;

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || busy || nameError) return;
    setBusy(true);
    setSubmitError("");
    const finalName = (name || "stream" + Math.floor(Math.random() * 100000)).toLowerCase();
    const logoUrl = logo.trim() || DEFAULT_LOGO;
    trackClick("button_click-add_stream");

    try {
      if (name) await axios.get(`${API_BASE_URL}/streamdesign/IsStreamNameAllowed`, { params: { streamName: finalName } });
    } catch (error) {
      setBusy(false);
      setNameError(errorText(error, "That stream name can't be used."));
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/stream/CreateStreamWithStyle`, { name: finalName, logoUrl, bannerColor: color });
      await axios.post(`${API_BASE_URL}/streamDesign/AddValues`, { streamName: finalName, uid: user.uid, streamColor: color, logoUrl }, await loginHeader());
      await reload();
      select(finalName);
      navigate("/");
    } catch (error) {
      console.error("Could not add the stream:", error);
      setSubmitError(errorText(error, "We couldn't create the stream. Please try again."));
      setBusy(false);
    }
  };

  const atLimit = !!quota && quota.used >= quota.limit;

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="streams" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-xl mx-auto space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Add a stream</h1>
              <p className="text-slate-500 mt-1 text-lg">Each stream has its own page, QR code, questions and history.</p>
            </div>

            {atLimit ? (
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 space-y-4">
                <p className="text-slate-700">
                  You're using all {quota!.limit} of your stream{quota!.limit === 1 ? "" : "s"}. You can ask for more, and extra streams are free once approved.
                </p>
                <Link to="/streams" className="btn-primary inline-block">Request more streams</Link>
              </div>
            ) : (
              <form onSubmit={create} className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 space-y-6">
                <div>
                  <label htmlFor="new-stream-name" className="block text-sm font-semibold text-slate-700 mb-2 ml-1">Stream name</label>
                  <input
                    id="new-stream-name"
                    value={name}
                    onChange={(e) => onName(e.target.value)}
                    placeholder="Leave empty to auto-generate"
                    className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-4 text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  {nameError && <p role="alert" className="text-red-600 text-sm mt-1 ml-1">{nameError}</p>}
                </div>
                <div>
                  <label htmlFor="new-stream-logo" className="block text-sm font-semibold text-slate-700 mb-2 ml-1">Logo URL <span className="font-normal text-slate-400">(optional)</span></label>
                  <input
                    id="new-stream-logo"
                    value={logo}
                    onChange={(e) => setLogo(e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-4 text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <ColorField
                  id="new-stream-color"
                  label="Brand color"
                  placeholder="Hex code, e.g. #000000. Your logo is shown on this color."
                  value={color}
                  onChange={setColor}
                />
                {submitError && (
                  <div role="alert" className="p-3 rounded-2xl bg-red-50 text-red-700 border border-red-100 text-sm font-medium flex items-center gap-2">
                    <AlertCircle size={18} /> {submitError}
                  </div>
                )}
                <button type="submit" disabled={busy || !!nameError || !quota} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy && <Loader2 size={18} className="animate-spin" />} {busy ? "Creating..." : "Create stream"}
                </button>
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AddStreamPage;
